import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MenuCategory from '../components/menu/MenuCategory';
import MenuFilter from '../components/menu/MenuFilter';
import { useLanguage } from '../context/LanguageContext';
import { menuCategories, getItemsByCategory, filterByDietary } from '../data/menu';

const dietaryFilters = [
  {
    id: 'vegetarian',
    label: { de: 'Vegetarisch', en: 'Vegetarian' },
    activeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  },
  {
    id: 'vegan',
    label: { de: 'Vegan', en: 'Vegan' },
    activeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
  },
  {
    id: 'glutenFree',
    label: { de: 'Glutenfrei', en: 'Gluten-free' },
    activeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
  }
];

export default function Menu() {
  const { t } = useLanguage();
  const location = useLocation();
  const hashCategory = location.hash ? location.hash.slice(1) : null;
  const [selectedCategory, setSelectedCategory] = useState(hashCategory || 'all');
  const [selectedDietary, setSelectedDietary] = useState([]);

  // Scroll to hash target on mount/hash change
  useEffect(() => {
    if (hashCategory) {
      const element = document.getElementById(hashCategory);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hashCategory]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId !== 'all') {
      const element = document.getElementById(categoryId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleDietaryChange = (filterId) => {
    setSelectedDietary(prev =>
      prev.includes(filterId)
        ? prev.filter(f => f !== filterId)
        : [...prev, filterId]
    );
  };

  const getFilteredCategories = () => {
    if (selectedCategory === 'all') {
      return menuCategories;
    }
    return menuCategories.filter(cat => cat.id === selectedCategory);
  };

  const getFilteredItems = (categoryId) => {
    const items = getItemsByCategory(categoryId);
    return filterByDietary(items, selectedDietary);
  };

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-primary-800 dark:bg-primary-900 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('menuPage.title')}
          </h1>
          <p className="text-lg text-primary-200 max-w-2xl mx-auto">
            {t('menuPage.subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <MenuFilter
          categories={menuCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          dietaryFilters={dietaryFilters}
          selectedDietary={selectedDietary}
          onDietaryChange={handleDietaryChange}
        />

        {/* Menu Categories */}
        {getFilteredCategories().map((category) => {
          const items = getFilteredItems(category.id);
          if (items.length === 0) return null;

          return (
            <MenuCategory
              key={category.id}
              category={category}
              items={items}
              isOpen={selectedCategory === 'all' || selectedCategory === category.id}
            />
          );
        })}

        {/* No results message */}
        {selectedDietary.length > 0 && getFilteredCategories().every(
          cat => getFilteredItems(cat.id).length === 0
        ) && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              Keine Gerichte gefunden, die Ihren Filterkriterien entsprechen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
